import { useMemo } from "react";
import { usePagesContent } from "../../../../Hooks/usePagesContent";
import {
  RUL_UNAVAILABLE_LABEL,
  splitUnavailableEntry,
  parseRangeBuckets,
  appendUnavailableBucket,
  isServerRangeEntry,
  classifyBucketValue,
  resolveBucketName,
} from "./unavailableBucket";
import { RUL_BUCKET_KEYS } from "./rulConstants";

// ─── Fallbacks used when org meta bucket definitions are absent ───────────────
// Each entry must have label, min, and color so the UI works without org meta.
const FALLBACK_BUCKETS = {
  months: [
    { label: "4-6 Years",   min: 48, color: "#B3E5FC" },
    { label: "2-4 Years",   min: 24, color: "#4FC3F7" },
    { label: "1-2 Years",   min: 12, color: "#0288D1" },
    { label: "6-12 Months", min:  6, color: "#01579B" },
    { label: "1-6 Months",  min:  1, color: "#FF9800" },
    { label: "<1 Month",    min:  0, color: "#E64A19" },
  ],
  cycles: [
    { label: ">6000",     min: 6000, color: "#B3E5FC" },
    { label: "4000-6000", min: 4000, color: "#4FC3F7" },
    { label: "2000-4000", min: 2000, color: "#0288D1" },
    { label: "1000-2000", min: 1000, color: "#01579B" },
    { label: "500-1000",  min:  500, color: "#FF9800" },
    { label: "<500",      min:    0, color: "#E64A19" },
  ],
  km: [
    { label: ">90,000",       min: 90000, color: "#B3E5FC" },
    { label: "60,000-90,000", min: 60000, color: "#4FC3F7" },
    { label: "30,000-60,000", min: 30000, color: "#0288D1" },
    { label: "15,000-30,000", min: 15000, color: "#01579B" },
    { label: "5,000-15,000",  min:  5000, color: "#FF9800" },
    { label: "<5,000",        min:     0, color: "#E64A19" },
  ],
};

// Range buckets for one unit (org meta or fallback) + "RUL Unavailable" last.
// Colour and label come from the org meta entry (grey when it has no colour). Both flat
// { label, min, color } and nested { label, range: { min, max }, color } entries parse;
// malformed entries are dropped by splitUnavailableEntry (see isUsableRangeEntry), and
// the fallback is used when none is usable.
function buildUnitBuckets(orgMetaBuckets, fallback) {
  const { rangeEntries, unavailableEntry } = splitUnavailableEntry(orgMetaBuckets, RUL_UNAVAILABLE_LABEL);
  return appendUnavailableBucket(parseRangeBuckets(rangeEntries, fallback), RUL_UNAVAILABLE_LABEL, unavailableEntry);
}

/**
 * Pure builder behind useRULBuckets (exported for tests).
 * @param {object} batteryIntelligence - metaInfo.BatteryIntelligence
 */
export function buildRULBuckets(batteryIntelligence = {}) {
  return {
    months: buildUnitBuckets(batteryIntelligence?.RULMonthsBucket, FALLBACK_BUCKETS.months),
    cycles: buildUnitBuckets(batteryIntelligence?.RULCyclesBucket, FALLBACK_BUCKETS.cycles),
    km:     buildUnitBuckets(batteryIntelligence?.RULKmBucket,     FALLBACK_BUCKETS.km),
  };
}

/**
 * Returns bucket config for each RUL unit, derived from org meta.
 * Each unit entry is an array of { name, displayLabel, color, min } sorted best -> worst,
 * followed by the "RUL Unavailable" bucket ({ ..., isUnavailable: true }).
 * `name` is the API value (filters, groupBy labels); `displayLabel` is what the UI shows.
 *
 * Color and label are read from org meta bucket entries directly —
 * no UI-side label-to-color mapping. Changing labels or colors in org meta
 * requires no UI code changes.
 *
 * Reads from: BatteryIntelligence.RULMonthsBucket / RULCyclesBucket / RULKmBucket
 * Expected org meta entry shape (the one the backend accepts):
 *   { "label": "4-6 Years", "color": "#B3E5FC", "range": { "min": 48, "max": 72 } }
 * Flat { label, min, max } is parsed by the frontend but ignored by the backend.
 * An entry with "includeNull": true sets the unavailable bucket's display label and colour.
 */
export function useRULBuckets() {
  const pagesContent        = usePagesContent();
  const batteryIntelligence = pagesContent?.metaInfo?.BatteryIntelligence || {};
  const { RULMonthsBucket, RULCyclesBucket, RULKmBucket } = batteryIntelligence;

  return useMemo(
    () => buildRULBuckets({ RULMonthsBucket, RULCyclesBucket, RULKmBucket }),
    [RULMonthsBucket, RULCyclesBucket, RULKmBucket]
  );
}

/**
 * Classifies a numeric RUL value into a bucket name.
 * Null / NaN / negative values map to the unavailable bucket when the list has one.
 * @param {number} value   - Raw RUL value (months / cycles / km)
 * @param {Array}  buckets - Bucket array from useRULBuckets() for the active unit
 * @returns {string|null} bucket name, or null if unclassifiable
 */
export function classifyRULValue(value, buckets) {
  return classifyBucketValue(value, buckets);
}

/**
 * True when the backend would build at least one range branch from this org meta list
 * (mirrors _classifyOrgMetaBucketEntry / _iterOrgMetaRangeEntries): see
 * isServerRangeEntry. A null / non-array list has none. Flat { min, max } entries are
 * ignored by the backend, so they do not count; nor does an entry with any present
 * bound that is not a finite number, even next to a valid one.
 * @param {Array} list - org-level RULMonthsBucket / RULCyclesBucket
 * @returns {boolean}
 */
export function hasServerRangeEntry(list) {
  if (!Array.isArray(list)) return false;
  return list.some((entry) => isServerRangeEntry(entry, RUL_UNAVAILABLE_LABEL));
}

/**
 * Which RUL units may take their bucket label from the server (metaInfo.RUL*Bucket).
 * Mirrors the backend: only RULMonthsBucket / RULCyclesBucket are org-meta-driven CASE
 * expressions, read from the top-level orgMeta.metaInfo.BatteryIntelligence only (not
 * pageMeta, not nested page keys). A unit is trusted only when that list has a range
 * entry the CASE actually uses (hasServerRangeEntry); otherwise every in-range row comes
 * back null and must be classified client-side. km is never server-labelled: the backend
 * km bucket is constant-driven with its own labels (">90000"), which do not match the
 * frontend km buckets (">90,000").
 * @param {object} orgBatteryIntelligence - orgMeta.metaInfo.BatteryIntelligence (un-merged)
 * @returns {{ months: boolean, cycles: boolean, km: boolean }}
 */
export function getRULServerBucketUnits(orgBatteryIntelligence = {}) {
  return {
    months: hasServerRangeEntry(orgBatteryIntelligence?.RULMonthsBucket),
    cycles: hasServerRangeEntry(orgBatteryIntelligence?.RULCyclesBucket),
    km:     false,
  };
}

/**
 * Hook form of getRULServerBucketUnits for the current org. Reads
 * pagesContent.orgBatteryIntelligence (set by BatteryIntelligence.jsx from the org
 * response alone), never the merged metaInfo.BatteryIntelligence. When it is absent,
 * nothing is trusted and every unit is classified client-side.
 */
export function useRULServerBucketUnits() {
  const pagesContent = usePagesContent();
  const { RULMonthsBucket, RULCyclesBucket } = pagesContent?.orgBatteryIntelligence || {};
  return useMemo(
    () => getRULServerBucketUnits({ RULMonthsBucket, RULCyclesBucket }),
    [RULMonthsBucket, RULCyclesBucket]
  );
}

/**
 * Resolves a battery row's RUL bucket name for one unit.
 * With useServerLabel, metaInfo[RUL_BUCKET_KEYS[unit]] is authoritative (see
 * resolveBucketName: a null there means "unclassified", not "classify locally").
 * Without it (km, or no usable org-level range for the unit) the value is classified client-side.
 * @param {object|null} metaInfo       - the row's response metaInfo
 * @param {string}      unit           - "months" | "cycles" | "km"
 * @param {number}      value          - raw RUL value for that unit
 * @param {Array}       buckets        - bucket array for that unit
 * @param {boolean}     useServerLabel - from getRULServerBucketUnits()[unit]
 * @returns {string|null}
 */
export function resolveRULCategory(metaInfo, unit, value, buckets, useServerLabel) {
  return resolveBucketName(useServerLabel ? metaInfo : null, RUL_BUCKET_KEYS[unit], value, buckets);
}
